import React, { useEffect, useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

const statusLabel = (status) => ({
  requested: "Request received",
  reviewing: "Under review",
  completed: "Completed",
  declined: "Closed",
}[status] || "Request received");

export const AccountDeletionRequest = () => {
  const [request, setRequest] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/me/account-deletion-request")
      .then((response) => setRequest(response.data.request))
      .catch(() => setRequest(null))
      .finally(() => setLoaded(true));
  }, []);

  const submit = async () => {
    if (!confirmed) {
      toast.error("Please confirm that you want us to review your deletion request.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/me/account-deletion-requests");
      setRequest(data.request);
      toast.success(
        data.already_requested ? "Your request is already under review." : "Request sent to Eira.",
      );
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSubmitting(false);
    }
  };

  if (!loaded) {
    return null;
  }

  if (request) {
    const requestedDate = new Date(request.requested_at).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    return (
      <section
        className="border border-[#D9C3A5] bg-[#FFF8EC] p-5"
        data-testid="account-deletion-request-status"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="mt-0.5 shrink-0 text-[#8C6D46]" aria-hidden="true" />
          <div>
            <h2 className="font-serif text-xl font-semibold text-[#1E2A24]">
              {statusLabel(request.status)}
            </h2>
            <p className="mt-2 text-sm leading-6 text-[#64748B]">
              {`Your account deletion request was received on ${requestedDate}. `}
              We will contact you if we need anything else.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="border border-[#E2DCD2] bg-[#FDFBF7] p-5"
      data-testid="account-deletion-request-panel"
    >
      <div className="flex items-start gap-3">
        <Trash2 size={20} className="mt-0.5 shrink-0 text-[#8C6D46]" aria-hidden="true" />
        <div>
          <h2 className="font-serif text-xl font-semibold text-[#1E2A24]">
            Request Account Deletion
          </h2>
          <p className="mt-2 text-sm leading-6 text-[#64748B]">
            We will review your request and remove or anonymise data that is no longer required for
            legal, tax, order or fraud-prevention purposes.
          </p>
        </div>
      </div>
      <label
        className="mt-4 flex cursor-pointer items-start gap-3 text-sm leading-5 text-[#1E2A24]"
        data-testid="account-deletion-confirmation-label"
      >
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
          className="mt-0.5 h-4 w-4 accent-[#2D5A47]"
          data-testid="account-deletion-confirmation-checkbox"
        />
        <span>I understand this sends a deletion request to Eira for review.</span>
      </label>
      <button
        type="button"
        onClick={submit}
        disabled={!confirmed || submitting}
        className={[
          "mt-5 border border-[#7F1D1D] px-4 py-2.5 text-sm font-semibold text-[#7F1D1D]",
          "hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50",
        ].join(" ")}
        data-testid="account-deletion-request-submit"
      >
        {submitting ? "Sending request…" : "Request account deletion"}
      </button>
    </section>
  );
};